import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api, { formatDetail } from "@/lib/api";

/**
 * Notification preferences panel — collapsible section di ProfileDialog.
 * User bisa on/off per kategori (task, task_comment, leave, access, announcement, info).
 * Backend enforce via `_create_notification` (silent drop kalau category off).
 */
export default function NotificationPrefsSection({ open }) {
  const [categories, setCategories] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null); // key sedang di-save

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/auth/notif-prefs")
      .then(({ data }) => { setCategories(data.categories || []); setPrefs(data.prefs || {}); })
      .catch((e) => toast.error(formatDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = async (key, next) => {
    const previous = prefs[key];
    // Optimistic update
    setPrefs((p) => ({ ...p, [key]: next }));
    setSaving(key);
    try {
      const nextPrefs = { ...prefs, [key]: next };
      await api.patch("/auth/notif-prefs", { prefs: nextPrefs });
    } catch (e) {
      // Rollback on failure
      setPrefs((p) => ({ ...p, [key]: previous }));
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menyimpan preferensi");
    } finally {
      setSaving(null);
    }
  };

  const enableAll = async () => {
    const next = Object.fromEntries(categories.map((c) => [c.key, true]));
    setPrefs(next);
    try { await api.patch("/auth/notif-prefs", { prefs: next }); toast.success("Semua notifikasi dinyalakan"); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const disableAll = async () => {
    const next = Object.fromEntries(categories.map((c) => [c.key, false]));
    setPrefs(next);
    try { await api.patch("/auth/notif-prefs", { prefs: next }); toast.success("Semua notifikasi dimatikan"); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="text-[11px] italic text-slate-400 px-3 py-4 text-center">Memuat preferensi…</div>;
  if (categories.length === 0) return <div className="text-[11px] italic text-slate-400 px-3 py-4 text-center">Tidak ada kategori notifikasi</div>;

  const allOn = categories.every((c) => prefs[c.key] !== false);
  const allOff = categories.every((c) => prefs[c.key] === false);

  return (
    <div className="border border-slate-200" data-testid="notif-prefs-panel">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 text-[10px]">
        <span className="uppercase tracking-wider font-semibold text-slate-500">
          {allOn ? "Semua Aktif" : allOff ? "Semua Nonaktif" : `${categories.filter((c) => prefs[c.key] !== false).length} dari ${categories.length} aktif`}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            data-testid="notif-prefs-enable-all"
            onClick={enableAll}
            disabled={allOn}
            className="px-2 py-0.5 text-[9px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-600 hover:border-emerald-500 hover:text-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >Nyalakan Semua</button>
          <button
            type="button"
            data-testid="notif-prefs-disable-all"
            onClick={disableAll}
            disabled={allOff}
            className="px-2 py-0.5 text-[9px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-600 hover:border-rose-500 hover:text-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >Matikan Semua</button>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {categories.map((c) => {
          const on = prefs[c.key] !== false;
          const Icon = on ? Bell : BellOff;
          return (
            <li key={c.key} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-slate-50" data-testid={`notif-pref-row-${c.key}`}>
              <div className="flex items-start gap-2 min-w-0">
                <Icon size={13} className={`shrink-0 mt-0.5 ${on ? "text-emerald-600" : "text-slate-400"}`} />
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#0B1D3A]">{c.label}</div>
                  <div className="text-[10px] text-slate-500 leading-snug mt-0.5">{c.description}</div>
                </div>
              </div>
              <Switch
                checked={on}
                onCheckedChange={(v) => toggle(c.key, v)}
                disabled={saving === c.key}
                data-testid={`notif-pref-toggle-${c.key}`}
                aria-label={`Toggle ${c.label}`}
              />
            </li>
          );
        })}
      </ul>
      <div className="px-3 py-1.5 text-[9px] italic text-slate-400 bg-slate-50 border-t border-slate-200">
        Perubahan langsung tersimpan. Notifikasi dari kategori yang dimatikan tidak akan muncul di lonceng maupun realtime push.
      </div>
    </div>
  );
}
